import { sharedToBalanceInWei } from '@shared/utils/formatter'
import { toUtf8, WasmContractQueryResponse } from '@injectivelabs/sdk-ts'
import { BASIS_POINT_DECIMAL_PLACES } from '@/app/utils/constant'
import {
  VaultCPMMConfig,
  VaultBaseConfig,
  VaultSpotConfig,
  VaultDerivativeConfig,
  LaunchpadFactoryConfig,
  OffChainVaultResponse,
  StakingConfigResponse,
  VaultMarketMakingConfig,
  OffChainVaultSpotConfig,
  AllocatorConfigResponse,
  OffChainVaultSpotResponse,
  OffChainVaultDerivativeConfig,
  VaultContractCPMMConfigResponse,
  LaunchpadFactoryConfigResponse,
  VaultContractBaseConfigResponse,
  OffChainVaultDerivativeResponse,
  VaultContractSpotConfigResponse,
  VaultContractMarketMakingResponse,
  VaultContractDerivativeConfigResponse
} from '@/types'

/**
 * @hidden
 */

const formatToString = (value?: string | number) =>
  value ? value.toString() : ''

export class MitoConfigTransformer {
  static baseConfigResponseToBaseConfig(
    config: VaultContractBaseConfigResponse
  ): VaultBaseConfig {
    return {
      owner: formatToString(config.owner),
      marketId: formatToString(config.market_id),
      subaccountId: formatToString(config.subaccount_id),
      feeRecipient: formatToString(config.fee_recipient),
      masterAddress: formatToString(config.master_address),
      orderDensity: Number(config.order_density),
      notionalValueCap: formatToString(config.notional_value_cap)
    }
  }

  static marketMakingResponseToMarketMaking(
    config: VaultContractMarketMakingResponse
  ): VaultMarketMakingConfig {
    return {
      reservationPriceSensitivityRatio: formatToString(
        config.reservation_price_sensitivity_ratio
      ),
      reservationSpreadSensitivityRatio: formatToString(
        config.reservation_spread_sensitivity_ratio
      ),
      maxActiveCapitalUtilizationRatio: formatToString(
        config.max_active_capital_utilization_ratio
      ),
      headChangeToleranceRatio: formatToString(
        config.head_change_tolerance_ratio
      ),
      headToTailDeviationRatio: formatToString(
        config.head_to_tail_deviation_ratio
      ),
      signedMinHeadToFairPriceDeviationRatio: formatToString(
        config.signed_min_head_to_fair_price_deviation_ratio
      ),
      signedMinHeadToTobDeviationRatio: formatToString(
        config.signed_min_head_to_tob_deviation_ratio
      ),
      defaultMidPriceVolatilityRatio: formatToString(
        config.default_mid_price_volatility_ratio
      ),
      minOracleVolatilitySampleSize: Number(
        config.min_oracle_volatility_sample_size
      ),
      oracleVolatilityMaxAge: Number(config.oracle_volatility_max_age),
      emergencyOracleVolatilitySampleSize: Number(
        config.emergency_oracle_volatility_sample_size
      ),
      lastValidMarkPrice: formatToString(config.last_valid_mark_price),
      minVolatilityRatio: formatToString(config.min_volatility_ratio),
      oracleStaleTime: Number(config.oracle_stale_time)
    }
  }

  static configResponseToCPMMVaultConfig(
    response: WasmContractQueryResponse
  ): VaultCPMMConfig {
    const { config } = JSON.parse(
      toUtf8(response.data)
    ) as VaultContractCPMMConfigResponse

    const [pricingStrategyType, pricingStrategyAmount] = Object.entries(
      config.pricing_strategy
    )[0]

    return {
      base: MitoConfigTransformer.baseConfigResponseToBaseConfig(config.base),
      pricingStrategyType,
      pricingStrategyAmount: JSON.stringify(pricingStrategyAmount),
      baseDecimals: Number(config.base_decimals),
      quoteDecimals: Number(config.quote_decimals),
      maxInvariantSensitivityBps: sharedToBalanceInWei({
        value: config.max_invariant_sensitivity_bps,
        decimalPlaces: -BASIS_POINT_DECIMAL_PLACES
      }).toNumber(),
      maxPriceSensitivityBps: sharedToBalanceInWei({
        value: config.max_price_sensitivity_bps,
        decimalPlaces: -BASIS_POINT_DECIMAL_PLACES
      }).toNumber(),
      feeBps: sharedToBalanceInWei({
        value: config.fee_bps,
        decimalPlaces: -BASIS_POINT_DECIMAL_PLACES
      }).toNumber(),
      orderType: config.order_type
    }
  }

  static configResponseToDerivativeVaultConfig(
    response: WasmContractQueryResponse
  ): VaultDerivativeConfig {
    const { config } = JSON.parse(
      toUtf8(response.data)
    ) as VaultContractDerivativeConfigResponse

    return {
      base: MitoConfigTransformer.baseConfigResponseToBaseConfig(config.base),
      marketMaking: MitoConfigTransformer.marketMakingResponseToMarketMaking(
        config.market_making
      ),
      leverage: formatToString(config.leverage),
      minProximityToLiquidation: formatToString(
        config.min_proximity_to_liquidation
      ),
      allowedRedemptionTypes: Number(config.allowed_redemption_types),
      positionPnlPenalty: formatToString(config.position_pnl_penalty)
    }
  }

  static configResponseToSpotVaultContractConfig(
    response: WasmContractQueryResponse
  ): VaultSpotConfig {
    const { config } = JSON.parse(
      toUtf8(response.data)
    ) as VaultContractSpotConfigResponse

    return {
      base: MitoConfigTransformer.baseConfigResponseToBaseConfig(config.base),
      marketMaking: MitoConfigTransformer.marketMakingResponseToMarketMaking(
        config.market_making
      ),
      oracleType: Number(config.oracle_type),
      targetBaseWeight: formatToString(config.target_base_weight),
      allowedRedemptionTypes: Number(config.allowed_redemption_types),
      baseDecimals: Number(config.base_decimals),
      quoteDecimals: Number(config.quote_decimals),
      baseOracleSymbol: formatToString(config.base_oracle_symbol),
      quoteOracleSymbol: formatToString(config.quote_oracle_symbol)
    }
  }

  static configResponseToOffChainVaultConfig(
    response: WasmContractQueryResponse
  ): OffChainVaultSpotConfig | OffChainVaultDerivativeConfig {
    const config = JSON.parse(toUtf8(response.data)) as OffChainVaultResponse

    const isDerivativeVault =
      (config.vault_type as OffChainVaultDerivativeResponse).Derivative !==
      undefined
    const derivativeConfig = (
      config.vault_type as OffChainVaultDerivativeResponse
    ).Derivative
    const spotConfig = (config.vault_type as OffChainVaultSpotResponse).Spot

    return {
      base: {
        admin: formatToString(config.admin),
        marketId: formatToString(config.market_id),
        vaultSubaccountId: formatToString(config.vault_subaccount_id),
        oracleStaleTime: Number(config.oracle_stale_time),
        notionalValueCap: formatToString(config.notional_value_cap),
        withdrawalAdminFeeBps: sharedToBalanceInWei({
          value: config.withdrawal_admin_fee_bps,
          decimalPlaces: -BASIS_POINT_DECIMAL_PLACES
        }).toNumber(),
        injRewardCommissionBps: sharedToBalanceInWei({
          value: config.inj_reward_commission_bps,
          decimalPlaces: -BASIS_POINT_DECIMAL_PLACES
        }).toNumber()
      },
      ...(isDerivativeVault
        ? {
            positionPnlPenalty: formatToString(
              derivativeConfig.position_pnl_penalty
            ),
            allowedDerivativeRedemptionTypes: Number(
              derivativeConfig.allowed_derivative_redemption_types
            )
          }
        : {
            oracleType: Number(spotConfig.oracle_type),
            baseOracleSymbol: formatToString(spotConfig.base_oracle_symbol),
            quoteOracleSymbol: formatToString(spotConfig.quote_oracle_symbol),
            baseDecimals: Number(spotConfig.base_decimals),
            quoteDecimals: Number(spotConfig.quote_decimals)
          })
    }
  }

  static configResponseToAllocatorConfig(response: WasmContractQueryResponse) {
    const config = JSON.parse(toUtf8(response.data)) as AllocatorConfigResponse

    return {
      owner: formatToString(config.owner),
      stakingContractAddress: formatToString(config.staking_contract_address),
      maxRewardDenomsPerGauge: config.max_reward_denoms_per_gauge
        ? Number(config.max_reward_denoms_per_gauge)
        : undefined,
      minGaugeDurationInSeconds: config.min_gauge_duration_in_seconds
        ? Number(config.min_gauge_duration_in_seconds)
        : undefined,
      maxActiveGaugesPerLpToken: config.max_active_gauges_per_lp_token
        ? Number(config.max_active_gauges_per_lp_token)
        : undefined,
      gaugeAllocationFeeDenom: formatToString(
        config.gauge_allocation_fee?.denom
      ),
      gaugeAllocationFeeAmount: formatToString(
        config.gauge_allocation_fee?.amount
      )
    }
  }

  static configResponseToStakingConfig(response: WasmContractQueryResponse) {
    const config = JSON.parse(toUtf8(response.data)) as StakingConfigResponse

    return {
      owner: formatToString(config.owner),
      lockupPeriod: Number(config.lockup_period || 0),
      allocatorContractAddress: formatToString(
        config.allocator_contract_address
      )
    }
  }

  static configResponseToLaunchpadFactoryConfig(
    response: WasmContractQueryResponse
  ): LaunchpadFactoryConfig {
    const { config } = JSON.parse(toUtf8(response.data)) as {
      config: LaunchpadFactoryConfigResponse
    }

    return {
      owner: config.owner,
      launchpadCodeId: config.launchpad_code_id,
      defaultStakedToSubscription: Object.entries(
        config.default_staked_to_subscription
      ).map(([stakedAmount, subscribableAmount]) => ({
        stakedAmount,
        subscribableAmount
      })),
      masterAddress: config.master_address,
      quoteDecimals: config.quote_decimals,
      mitoTreasuryAddress: config.mito_treasury_address,
      launchFee: config.launch_inj_fee,
      launchpadsQuoteDenom: config.launchpads_quote_denom,
      mitoTreasuryFeeBasisPoints: config.mito_treasury_fee_basis_points,
      vestingCodeId: config.vesting_code_id
    }
  }
}
